/**
 * La serie de cada equipo, al recibirlo: el IMEI de un celular o la serie de
 * una laptop (módulo `serials`, migración 230; docs/15 §2.41).
 *
 * Las mismas reglas que el servidor (`serial-number.ts`): se compara sin
 * espacios y en mayúsculas, lleva de 3 a 40 letras, cifras, guiones, puntos o
 * barras, y quince dígitos solo entran si cumplen Luhn —casi siempre es un
 * IMEI mal leído, y una serie equivocada es una garantía que no se encuentra—.
 * Se comprueban aquí para decirlo al escanear, no al confirmar treinta.
 */

export function normalizarSerie(texto: string): string {
  return texto.trim().replace(/\s+/g, '').toUpperCase();
}

export function cumpleLuhn(digitos: string): boolean {
  let suma = 0;
  for (let i = 0; i < digitos.length; i += 1) {
    let d = digitos.charCodeAt(i) - 48;
    if ((digitos.length - i) % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    suma += d;
  }
  return suma % 10 === 0;
}

/** Por qué no es una serie, o nulo si lo es. */
export function problemaDeSerie(serie: string): string | null {
  if (serie.length < 3 || serie.length > 40) return 'La serie tiene entre 3 y 40 caracteres.';
  if (!/^[A-Z0-9][A-Z0-9\-./]*$/.test(serie)) {
    return 'La serie solo lleva letras, cifras, guiones, puntos o barras.';
  }
  if (/^\d{15}$/.test(serie) && !cumpleLuhn(serie)) {
    return 'Tiene la forma de un IMEI pero su último dígito no cuadra: vuelve a leerlo.';
  }
  return null;
}

/** El estado de conservación de **este** equipo, como lo guarda el servidor. */
export const ESTADOS_DEL_EQUIPO = [
  { valor: 'new', etiqueta: 'Nuevo' },
  { valor: 'open_box', etiqueta: 'Caja abierta' },
  { valor: 'refurbished', etiqueta: 'Reacondicionado' },
  { valor: 'used', etiqueta: 'Usado' },
] as const;

export type EstadoDelEquipo = (typeof ESTADOS_DEL_EQUIPO)[number]['valor'];

/**
 * Lo que pasa al leer una serie en la lista de la recepción: entra, ya estaba
 * leída (la pistola leyó dos veces la misma caja) o no es una serie.
 */
export function agregarSerie(
  leidas: readonly string[],
  texto: string,
): { series: string[]; problema: string | null } {
  const serie = normalizarSerie(texto);
  if (serie === '') return { series: [...leidas], problema: null };

  const problema = problemaDeSerie(serie);
  if (problema !== null) return { series: [...leidas], problema: `«${serie}»: ${problema}` };
  if (leidas.includes(serie)) {
    return { series: [...leidas], problema: `La serie ${serie} ya está en esta recepción.` };
  }
  return { series: [...leidas, serie], problema: null };
}

/**
 * «Checa tu IMEI» de OSIPTEL, la consulta pública en gob.pe. Un equipo usado
 * o reacondicionado puede venir reportado como robado: el enlace la abre, y la
 * decisión es de quien recibe (12 oct, como en la caja).
 */
export const CONSULTA_DE_IMEI_OSIPTEL = 'https://www.gob.pe/1170';

/**
 * Si un IMEI parece de otro modelo que los ya leídos (12 oct): sus primeros
 * ocho dígitos (el TAC) dicen el modelo, y no coincide con ninguno. Diez cajas
 * de A55 y una de A35 entraban todas como A55. Es un aviso, no un rechazo: el
 * mismo modelo puede salir con dos TAC. La serie de una laptop no se compara.
 */
export function pareceDeOtroModelo(imei: string, delMismoModelo: readonly string[]): boolean {
  const esImei = (texto: string) => /^\d{15}$/.test(texto) && cumpleLuhn(texto);
  if (!esImei(imei)) return false;
  const tacs = delMismoModelo.filter((otro) => otro !== imei && esImei(otro)).map((otro) => otro.slice(0, 8));
  return tacs.length > 0 && !tacs.includes(imei.slice(0, 8));
}

/** Un equipo de la recepción: su número y, si se leyó, el segundo. */
export interface EquipoLeido {
  serie: string;
  segunda: string;
}

/**
 * Leer con «Dos IMEI por equipo» (12 oct): cada caja de un celular de dos
 * chips trae dos IMEI, y teclear el segundo en su fila no se hacía —la
 * garantía después no encontraba el equipo por el número que el cliente traía
 * a mano—. Con esto la pistola lee los dos seguidos: la lectura que sigue a un
 * equipo sin segundo número es ese segundo número, y la siguiente, otro equipo.
 */
export function agregarLectura(
  equipos: readonly EquipoLeido[],
  texto: string,
  dosImeiPorEquipo: boolean,
): { equipos: EquipoLeido[]; problema: string | null; nuevo: boolean; otroModelo: boolean } {
  const ultimo = equipos.at(-1);
  const esperaSegundo = dosImeiPorEquipo && ultimo !== undefined && ultimo.segunda.trim() === '';
  const leidas = equipos.flatMap((uno) => (uno.segunda.trim() === '' ? [uno.serie] : [uno.serie, normalizarSerie(uno.segunda)]));
  const resultado = agregarSerie(leidas, texto);
  if (resultado.series.length === leidas.length) {
    return { equipos: [...equipos], problema: resultado.problema, nuevo: false, otroModelo: false };
  }
  const serie = resultado.series.at(-1)!;
  if (esperaSegundo) {
    return {
      equipos: [...equipos.slice(0, -1), { ...ultimo, segunda: serie }],
      problema: null,
      nuevo: false,
      otroModelo: false,
    };
  }
  // Solo el primer número de cada equipo: el IMEI 2 puede llevar otro TAC.
  const otroModelo = pareceDeOtroModelo(
    serie,
    equipos.map((uno) => uno.serie),
  );
  return { equipos: [...equipos, { serie, segunda: '' }], problema: null, nuevo: true, otroModelo };
}

/** Con dos IMEI por equipo, el último leído al que le falta el segundo. */
export function faltaSegundoImei(equipos: readonly EquipoLeido[], dosImeiPorEquipo: boolean): string | null {
  const ultimo = equipos.at(-1);
  if (!dosImeiPorEquipo || ultimo === undefined || ultimo.segunda.trim() !== '') return null;
  return `Falta el segundo IMEI de ${ultimo.serie}: léelo, o quita ese equipo de la lista.`;
}
